import { JobCardForm } from "@/pages/jobcards/types";

interface Props {
  form: JobCardForm;
  onChange: (patch: Partial<JobCardForm>) => void;
}

export default function SectionJobInfo({ form, onChange }: Props) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
        <span className="w-5 h-5 flex items-center justify-center">
          <i className="ri-hashtag text-amber-600 text-base" />
        </span>
        <h3 className="text-sm font-semibold text-gray-800">Section 2 &amp; 3 — Job Number &amp; Dates</h3>
      </div>
      <div className="p-5">
        {/* Job Number */}
        <div className="mb-5">
          <label className="block text-xs font-semibold text-gray-600 mb-2">
            Job Card Number <span className="text-amber-600">(Auto-generated)</span>
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1 flex items-center gap-3 px-4 py-3 bg-amber-50 border-2 border-amber-200 rounded-xl">
              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-barcode-line text-amber-600 text-lg" />
              </span>
              <span className="text-lg font-bold text-amber-700 tracking-wide font-mono">
                {form.jobNo || "—"}
              </span>
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard?.writeText(form.jobNo);
              }}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-gray-200 text-gray-400 hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50 cursor-pointer transition-all"
              title="Copy Job Number"
            >
              <i className="ri-file-copy-line text-base" />
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-1.5">
            Format: <span className="font-mono">[Branch]-[Prefix]-[YYYY]-[MM]-[Seq]</span>
          </p>
        </div>

        {/* Field Visit Required */}
        <div className="mb-5">
          <button
            type="button"
            onClick={() => onChange({ fieldVisitRequired: !form.fieldVisitRequired })}
            className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
              form.fieldVisitRequired
                ? "border-amber-400 bg-amber-50"
                : "border-gray-100 bg-gray-50 hover:border-gray-200 hover:bg-white"
            }`}
          >
            <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 border-2 transition-all ${
              form.fieldVisitRequired ? "bg-amber-500 border-amber-500" : "border-gray-300 bg-white"
            }`}>
              {form.fieldVisitRequired && <i className="ri-check-line text-white text-xs" />}
            </div>
            <div className="flex-1">
              <p className={`text-sm font-semibold ${
                form.fieldVisitRequired ? "text-amber-800" : "text-gray-700"
              }`}>
                Field Visit Required
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Check this if a site visit is needed — project &amp; client details will appear in the Site Visit page
              </p>
            </div>
            {form.fieldVisitRequired && (
              <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                <i className="ri-map-pin-line text-xs" />
                Visit Scheduled
              </span>
            )}
          </button>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Job Assigned Date <span className="text-red-400">*</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-calendar-check-line text-gray-400 text-sm" />
              </span>
              <input
                type="date"
                value={form.assignedDate}
                onChange={(e) => onChange({ assignedDate: e.target.value })}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Job Card Created Date
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                <i className="ri-calendar-line text-gray-400 text-sm" />
              </span>
              <input
                type="date"
                value={form.createdDate}
                readOnly
                className="w-full pl-9 pr-3 py-2.5 border border-gray-100 rounded-xl text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Auto-set to today</p>
          </div>
        </div>
      </div>
    </div>
  );
}
